import { useEffect } from "react";
import { useLocation } from "wouter";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle, XCircle, Users, Loader2 } from "lucide-react";
import { DEFAULT_PERMISSIONS } from "@shared/schema";
import type { TeamMember, AreaPermissions, AppArea } from "@shared/schema";
import { APP_AREAS } from "@shared/schema";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import { usePageTitle } from "@/hooks/use-page-title";

type TeamInvitation = TeamMember & { ownerName: string };

const AREA_LABELS: Record<AppArea, string> = {
  messages: "Messages",
  members: "Members",
  properties: "Properties",
  underwriting: "Underwriting",
  image_generator: "Image Generator",
  pricing: "Rooms/Pricing",
  maintenance: "Operations",
  financials: "Financials",
  devices: "Devices",
  furnishing: "Furnishing",
  team: "Team",
  invite: "Invite Teammates",
  padsplit_sync: "PadSplit Sync",
  owners: "Owners",
};

export default function AppAcceptInvitations() {
  const queryClient = useQueryClient();
  usePageTitle("Accept Invitation");
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const invitationsQuery = useQuery<TeamInvitation[]>({
    queryKey: ["/api/app/team-invitations"],
  });

  const respondMutation = useMutation({
    mutationFn: async ({ id, action }: { id: number; action: "accept" | "decline" }) => {
      await apiRequest("PATCH", `/api/app/team-invitations/${id}`, { action });
    },
    onSuccess: async (_data, variables) => {
      toast({
        title: variables.action === "accept" ? "Invitation accepted" : "Invitation declined",
        description: variables.action === "accept"
          ? "You now have access to their properties"
          : "The invitation has been declined",
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/app/my-permissions"] }),
        queryClient.refetchQueries({ queryKey: ["/api/app/team-invitations"] }),
      ]);
      const remaining = queryClient.getQueryData<TeamInvitation[]>(["/api/app/team-invitations"]) ?? [];
      if (remaining.length === 0) {
        navigate("/app/dashboard");
      }
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to respond to invitation", variant: "destructive" });
    },
  });

  const invitations = invitationsQuery.data ?? [];

  useEffect(() => {
    if (!invitationsQuery.isLoading && !invitationsQuery.isFetching && invitations.length === 0) {
      navigate("/app/dashboard");
    }
  }, [invitations.length, invitationsQuery.isLoading, invitationsQuery.isFetching, navigate]);

  if (invitationsQuery.isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-32 w-full max-w-lg" />
      </div>
    );
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        <div className="mb-8 flex justify-center">
          <img src={logoImg} alt="PadPulse" className="h-[50px] w-auto" data-testid="img-invite-logo" />
        </div>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 mb-4">
            <Users className="h-7 w-7 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-invite-heading">
            You've been invited
          </h1>
          <p className="text-gray-500 mt-2 text-sm">
            Please accept or decline {invitations.length === 1 ? "this invitation" : "these invitations"} before continuing.
          </p>
        </div>

        <div className="space-y-4" data-testid="list-invitations">
          {invitations.map((inv) => {
            const perms = (inv.permissions || DEFAULT_PERMISSIONS) as AreaPermissions;
            const enabledAreas = APP_AREAS.filter((a) => perms[a] !== "disabled");
            const isPending = respondMutation.isPending && respondMutation.variables?.id === inv.id;

            return (
              <Card key={inv.id} className="p-5 border-blue-100 bg-white shadow-md" data-testid={`card-invitation-${inv.id}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="font-semibold text-gray-900 text-base" data-testid={`text-inviter-name-${inv.id}`}>
                      {inv.ownerName}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      invited you as{" "}
                      <Badge variant="secondary" className="ml-1 capitalize" data-testid={`badge-role-${inv.id}`}>
                        {inv.role}
                      </Badge>
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => respondMutation.mutate({ id: inv.id, action: "accept" })}
                      disabled={respondMutation.isPending}
                      data-testid={`button-accept-${inv.id}`}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isPending && respondMutation.variables?.action === "accept" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><CheckCircle className="h-4 w-4 mr-1" />Accept</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => respondMutation.mutate({ id: inv.id, action: "decline" })}
                      disabled={respondMutation.isPending}
                      data-testid={`button-decline-${inv.id}`}
                    >
                      {isPending && respondMutation.variables?.action === "decline" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><XCircle className="h-4 w-4 mr-1" />Decline</>
                      )}
                    </Button>
                  </div>
                </div>

                {enabledAreas.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-100">
                    {enabledAreas.map((area) => (
                      <Badge key={area} variant="outline" className="text-xs text-gray-600">
                        {AREA_LABELS[area]}: {perms[area]}
                      </Badge>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          You must respond to all invitations before accessing your account.
        </p>
      </div>
    </div>
  );
}
